/* SalesLayout — top-level layout for the Sales Portal (/sales/*).
 *
 * iter98.52 — Mirrors the doctor portal's premium aesthetic:
 *   • Left rail nav with section icons + label + active-state pill
 *   • Top bar with brand, search, current-user chip
 *   • Outlet for child routes (Dashboard, Pipeline, Prospects, Settings)
 *
 * Access gate: only `sales`, `sales_manager`, `admin` roles can enter.
 * Any other role hitting /sales/* bounces back to their own portal.
 */
import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
    Gauge, Kanban, ListChecks, ChartBar, Gear, SignOut, MagnifyingGlass,
    CaretDown, CheckSquare, ShareNetwork,
} from "@phosphor-icons/react";


const NAV = [
    { to: "/sales", label: "Dashboard", icon: Gauge, end: true },
    { to: "/sales/pipeline", label: "Pipeline", icon: Kanban },
    { to: "/sales/prospects", label: "Prospects", icon: ListChecks },
    { to: "/sales/tasks", label: "Tasks", icon: CheckSquare, showBadge: true },
    { to: "/sales/analytics", label: "Analytics", icon: ChartBar },
    { to: "/sales/demo-links", label: "Demo Links", icon: ShareNetwork },
];


/* Color tokens — sales portal uses the same teal-cyan palette as the
   rest of Invisique for cohesion. */
const COLORS = {
    primary: "hsl(193 100% 22%)",
    primaryDark: "hsl(193 100% 14%)",
};


export default function SalesLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    // iter98.53 — Overdue badge poll. Refreshes every 60s + on first
    // mount so the sidebar always reflects the latest count without
    // requiring a page reload.
    const [overdueCount, setOverdueCount] = useState(0);

    // Role gate — bounce non-sales users back to their portal.
    useEffect(() => {
        if (loading || !user) return;
        const allowed = ["sales", "sales_manager", "admin"].includes(user.role);
        if (!allowed) {
            // Best-guess redirect; PortalRedirect will refine on next mount
            if (user.role === "doctor") navigate("/doctor", { replace: true });
            else if (user.role === "patient") navigate("/patient", { replace: true });
            else navigate("/", { replace: true });
        }
    }, [user, loading, navigate]);

    useEffect(() => {
        if (!user) return;
        let alive = true;
        const fetchCount = async () => {
            try {
                const r = await api.get("/sales/tasks");
                if (alive) setOverdueCount(r.data?.counts?.overdue || 0);
            } catch { /* swallow */ }
        };
        fetchCount();
        const iv = setInterval(fetchCount, 60000);
        return () => { alive = false; clearInterval(iv); };
    }, [user]);

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading sales portal…</div>;
    if (!user) {
        navigate("/login", { replace: true });
        return null;
    }

    const isManager = user.role === "sales_manager" || user.role === "admin";
    const visibleNav = NAV;

    const initials = (user.name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

    return (
        <div className="min-h-screen flex bg-[#F4F7F6]">
            {/* ─────────────────── Left rail ─────────────────── */}
            <aside
                className="hidden md:flex md:flex-col border-r border-border bg-white shrink-0 transition-all"
                style={{ width: collapsed ? 64 : 232 }}
                data-testid="sales-sidebar"
            >
                {/* Brand */}
                <div className="px-4 py-5 flex items-center gap-2 border-b border-border/60">
                    <div
                        className="w-8 h-8 rounded-lg grid place-items-center text-white font-bold shrink-0"
                        style={{ background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary})` }}
                    >
                        I
                    </div>
                    {!collapsed && (
                        <div className="min-w-0">
                            <div className="font-display text-sm font-bold leading-tight tracking-tight">Invisique™</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold leading-tight">Sales</div>
                        </div>
                    )}
                </div>

                {/* Nav */}
                <nav className="flex-1 px-2 py-3 space-y-0.5">
                    {visibleNav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={`sales-nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                                    isActive
                                        ? "bg-primary text-white"
                                        : "text-foreground hover:bg-secondary"
                                }`
                            }
                        >
                            <n.icon size={18} weight="duotone" />
                            {!collapsed && <span className="flex-1 truncate">{n.label}</span>}
                            {!collapsed && n.showBadge && overdueCount > 0 && (
                                <span
                                    className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold tabular-nums"
                                    data-testid="sales-nav-tasks-badge"
                                >
                                    {overdueCount > 99 ? "99+" : overdueCount}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Bottom — settings + sign out */}
                <div className="px-2 py-3 border-t border-border/60 space-y-0.5">
                    <NavLink
                        to="/sales/settings"
                        data-testid="sales-nav-settings"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                                isActive ? "bg-primary text-white" : "text-foreground hover:bg-secondary"
                            }`
                        }
                    >
                        <Gear size={18} weight="duotone" />
                        {!collapsed && <span>Settings</span>}
                    </NavLink>
                    <button
                        type="button"
                        onClick={() => { logout?.(); navigate("/sales-login"); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                        data-testid="sales-signout-btn"
                    >
                        <SignOut size={18} weight="duotone" />
                        {!collapsed && <span>Sign out</span>}
                    </button>
                </div>
            </aside>

            {/* ─────────────────── Main column ─────────────────── */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Top bar */}
                <header className="h-14 border-b border-border bg-white flex items-center justify-between px-4 sm:px-6 shrink-0">
                    <div className="text-xs text-muted-foreground tabular-nums">
                        Sales · {isManager ? "All India" : (user.territory || "—").toUpperCase()}
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-2 bg-secondary rounded-full px-3 h-8 w-72">
                            <MagnifyingGlass size={14} className="text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search prospects, doctors, clinics…"
                                className="bg-transparent text-xs outline-none flex-1 placeholder:text-muted-foreground"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        const v = e.target.value.trim();
                                        if (v) navigate(`/sales/prospects?q=${encodeURIComponent(v)}`);
                                    }
                                }}
                                data-testid="sales-search-input"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate("/sales/settings")}
                            className="flex items-center gap-2 text-xs"
                            data-testid="sales-user-chip"
                        >
                            <div className="w-7 h-7 rounded-full bg-primary/10 text-primary grid place-items-center font-bold text-[11px]">
                                {initials}
                            </div>
                            <div className="hidden sm:block text-left">
                                <div className="font-semibold leading-tight">{user.name || "—"}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider leading-tight">
                                    {user.role === "sales_manager" ? "Sales Manager" : user.role === "admin" ? "Admin" : "Sales & KAM"}
                                </div>
                            </div>
                            <CaretDown size={11} className="text-muted-foreground" />
                        </button>
                    </div>
                </header>

                {/* Route outlet */}
                <main className="flex-1 overflow-y-auto bg-[#F4F7F6]" data-testid="sales-main">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
